<script setup lang="ts">
import StatusBadge from "@comp-lib/components/StatusBadge.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faCheck, faXmark, faLinkSlash, faUserKey } from "@fortawesome/pro-regular-svg-icons";
import { useUserStore } from "@/stores/userStore";
import type { MobiusTenant } from "@/types";
import type { MobiusPlanType } from "@comp-lib/types";

const props = withDefaults(defineProps<{
  tenant?: MobiusTenant;
  admin?: boolean;
  current?: boolean;
  showUnlink?: boolean;
  unlinkDisabled?: boolean;
}>(), {
  tenant: undefined,
  admin: false,
  current: false,
  showUnlink: false,
  unlinkDisabled: false,
});

const emit = defineEmits<{
  select: [];
  unlink: [];
}>();

const { getPlanName } = useUserStore();

function getPlanClass(planId: string): MobiusPlanType {
  return getPlanName(planId);
}
</script>

<template>
  <article class="tenant-card-wrapper">
    <!-- Admin console variant -->
    <div v-if="props.admin" :class="['tenant-card', 'admin', { current: props.current }]" @click="emit('select')">
      <FontAwesomeIcon :icon="faUserKey" class="admin-icon" />
      <div class="name-plan">Admin console</div>
    </div>

    <!-- Regular tenant variant -->
    <template v-else-if="props.tenant">
      <div :class="['tenant-card', { current: props.current }]" @click="emit('select')">
        <div class="name-plan">
          {{ props.tenant.name }}
          <span
            v-if="getPlanName(props.tenant.subscription.plan_id)"
            :class="['plan-badge', getPlanClass(props.tenant.subscription.plan_id)]"
          >
            {{ getPlanName(props.tenant.subscription.plan_id) }}
          </span>
        </div>
        <StatusBadge
          :text="props.tenant.subscription.is_active ? 'Active' : 'Inactive'"
          :color="props.tenant.subscription.is_active ? 'green' : 'red'"
          :statusIcon="props.tenant.subscription.is_active ? faCheck : faXmark"
        />
      </div>
      <button
        v-if="props.showUnlink"
        v-tooltip="{ text: 'Unlink tenant' }"
        class="unlink-btn"
        :disabled="props.unlinkDisabled"
        @click.stop="emit('unlink')"
      >
        <FontAwesomeIcon :icon="faLinkSlash" />
      </button>
    </template>
  </article>
</template>

<style lang="scss" scoped>
.tenant-card-wrapper {
  display: flex;
  gap: 8px;
  .tenant-card {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    padding: 15px;
    border: 1px solid map.get(v.$gray, "300");
    border-radius: map.get(v.$border-radius, "sm");
    box-shadow: map.get(v.$shadow, "xs");
    cursor: pointer;
    transition: background-color 0.2s, box-shadow 0.2s;
    &:hover {
      background-color: map.get(v.$gray, "50");
      box-shadow: map.get(v.$shadow, "sm");
    }
    &.current {
      background-color: map.get(v.$primary, "25");
      border-color: map.get(v.$primary, "200");
    }
    &.admin {
      justify-content: center;
      gap: 12px;
      .admin-icon {
        font-size: 1.125rem;
        color: map.get(v.$gray, "400");
      }
    }
    .name-plan {
      font-weight: 600;
      font-size: 0.875rem;
      line-height: 1.4;
      color: map.get(v.$gray, "700");
      .plan-badge {
        display: inline-block;
        vertical-align: top;
        margin-left: 8px;
        font-size: 0.625rem;
        font-weight: 600;
        line-height: 1.2;
        padding: 2px 8px;
        text-transform: uppercase;
        letter-spacing: 0.05rem;
        border-radius: map.get(v.$border-radius, "max");
        &.standard {
          background-color: map.get(v.$primary, "50");
          color: map.get(v.$primary, "600");
        }
        &.lite {
          background-color: map.get(v.$secondary, "50");
          color: map.get(v.$secondary, "700");
        }
        &.free {
          background-color: map.get(v.$gray, "100");
          color: map.get(v.$gray, "600");
        }
        &.qbo {
          background-color: map.get(v.$success, "50");
          color: map.get(v.$success, "700");
        }
      }
    }
  }
  .unlink-btn {
    background-color: map.get(v.$info, "25");
    border: none;
    border-radius: map.get(v.$border-radius, "sm");
    color: map.get(v.$info, "500");
    cursor: pointer;
    padding: 5px 10px;
    font-size: 0.875rem;
    transition: background-color 0.2s, color 0.2s;
    &:hover {
      background-color: map.get(v.$info, "50");
      color: map.get(v.$info, "700");
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
